import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowReference } from "../../../common/controls";
import { publisherDisplayId } from "../../../publisher/store/PublisherCommonStore";
import { ShowNumber } from "../../../common/controls";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionDocumentationView as SnapshotEditionDocumentationView } from "./EditionDocumentationView";
import { PublisherView as SnapshotPublisherView } from "./PublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { MusicalWorkView as SnapshotMusicalWorkView } from "./MusicalWorkView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { FormControl } from "@mui/material";

type EditionContractPublisherViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const EditionContractPublisherView: React.FC<
  EditionContractPublisherViewProps
> = ({ snapshotData, snapshotEntityName, snapshotRecordId }) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop(
    "EditionContractPublisher"
  );
  const renderExtension = useSnapshotExtension("EditionContractPublisher");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "EditionDocumentation":
          return (
            <SnapshotEditionDocumentationView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Publisher":
          return (
            <SnapshotPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWork":
          return (
            <SnapshotMusicalWorkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <FormControl fullWidth>
        <ShowReference
          label={langStore.EditionContractPublisherPublisherLabel}
          selectedValue={
            snapshotData?.publisher
              ? langStore.displayIdByLang(
                  publisherDisplayId(snapshotData?.publisher, langStore)
                )
              : ""
          }
          onView={() => pushModal("Publisher", snapshotData?.publisher)}
        />
        <ShowNumber
          label={langStore.EditionContractPublisherPercentageLabel}
          value={snapshotData?.percentage ?? null}
          decimalScale={2}
        />
      </FormControl>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
    </>
  );
};
